import { useEffect, useRef, useState } from 'react';

export default function MobileApp() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Order Anywhere, Anytime
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Download our mobile app for the easiest way to browse menus, customize meals, and manage your subscription on the go.
            </p>

            {/* App Store Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="#"
                className="inline-flex items-center gap-3 px-6 py-4 bg-black text-white rounded-2xl hover:bg-gray-900 transition-all shadow-xl hover:shadow-2xl cursor-pointer"
              >
                <i className="ri-apple-fill text-3xl"></i>
                <div className="text-left">
                  <div className="text-xs">Download on the</div>
                  <div className="text-lg font-semibold">App Store</div>
                </div>
              </a>
              <a
                href="#"
                className="inline-flex items-center gap-3 px-6 py-4 bg-black text-white rounded-2xl hover:bg-gray-900 transition-all shadow-xl hover:shadow-2xl cursor-pointer"
              >
                <i className="ri-google-play-fill text-3xl"></i>
                <div className="text-left">
                  <div className="text-xs">Get it on</div>
                  <div className="text-lg font-semibold">Google Play</div>
                </div>
              </a>
            </div>

            <p className="text-sm text-white/70">
              Available on iOS 14+ and Android 8+
            </p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-6 mt-12">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0"></i>
                <span className="text-white/90">Easy meal browsing</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0"></i>
                <span className="text-white/90">Quick reordering</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0"></i>
                <span className="text-white/90">Track deliveries</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0"></i>
                <span className="text-white/90">Manage subscription</span>
              </div>
            </div>
          </div>

          {/* Right - Phone Mockup */}
          <div
            className={`relative transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative mx-auto max-w-sm">
              {/* Phone Frame */}
              <div className="relative transform rotate-6 hover:rotate-0 transition-transform duration-500">
                <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="bg-white rounded-[2.5rem] overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=modern%20mobile%20app%20interface%20showing%20food%20delivery%20menu%20with%20colorful%20meal%20cards%2C%20clean%20UI%20design%2C%20bright%20interface%2C%20smartphone%20screen%20mockup%2C%20professional%20app%20design%2C%20meal%20ordering%20interface%20with%20images%20and%20prices&width=400&height=800&seq=app-mockup-001&orientation=portrait"
                      alt="Mobile app interface"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-8 -left-8 w-24 h-24 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center animate-bounce">
                <i className="ri-star-fill text-yellow-400 text-3xl"></i>
              </div>
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
                <i className="ri-heart-fill text-red-400 text-4xl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}